import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';

import type { CustomerDetail } from '@domain/customer/Customer';
import { canActivate, canSuspend } from '@domain/customer/Customer';
import { apiClient } from '@infrastructure/api/client';
import { errorText } from '@infrastructure/api/errors';
import { HttpCustomerRepository } from '@infrastructure/api/HttpCustomerRepository';
import { BalanceAdjustModal } from '@app/components/BalanceAdjustModal';
import { CustomerEditModal } from '@app/components/CustomerEditModal';
import { ChangePlanModal } from '@app/components/ChangePlanModal';
import { formatDateTime } from '@app/lib/format';
import { StatusBadge } from '@app/components/StatusBadge';

const repo = new HttpCustomerRepository(apiClient);

export function SubscriberDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<CustomerDetail | null>(null);
  const [error, setError] = useState<{ kind: 'not_found' | 'other'; message: string } | null>(null);
  const [showBalance, setShowBalance] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showChangePlan, setShowChangePlan] = useState(false);

  const refresh = () => {
    if (!id) return;
    repo.get(id).then((d) => { setData(d); setError(null); }).catch((e) => {
      if (axios.isAxiosError(e) && e.response?.status === 404) {
        setError({ kind: 'not_found', message: 'Subscriber not found.' });
      } else {
        setError({ kind: 'other', message: (e as Error).message });
      }
    });
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (error) {
    return (
      <div className="rounded-lg border bg-white p-6 text-center shadow-sm">
        <p className="text-base text-slate-700">{error.message}</p>
        <p className="mt-3">
          <Link to="/subscribers" className="text-sm text-blue-600 hover:underline">← Back to Subscribers</Link>
        </p>
      </div>
    );
  }
  if (!data) return <div className="text-sm text-slate-500">Loading…</div>;

  const onSuspend = async () => {
    const reason = window.prompt('Suspend reason (logged):') ?? '';
    if (!reason.trim()) return;
    try {
      await repo.suspend(data.id, reason);
    } catch (e) {
      alert(errorText(e));
    }
    refresh();
  };
  const onActivate = async () => {
    const reason = window.prompt('Activate reason (logged):') ?? '';
    if (!reason.trim()) return;
    try {
      await repo.activate(data.id, reason);
    } catch (e) {
      alert(errorText(e));
    }
    refresh();
  };

  return (
    <section>
      <div className="mb-3">
        <Link to="/subscribers" className="text-sm text-blue-600 hover:underline">
          ← Subscribers
        </Link>
      </div>
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold">{data.company_name}</h2>
          <p className="text-sm text-slate-600">
            {data.user.email}{' '}
            <StatusBadge value={data.status} />
          </p>
        </div>
        <div className="flex flex-wrap gap-2 text-sm">
          {canSuspend(data) && (
            <button type="button" onClick={onSuspend} className="rounded border px-3 py-1 text-red-700">
              Suspend
            </button>
          )}
          {canActivate(data) && (
            <button type="button" onClick={onActivate} className="rounded border px-3 py-1 text-green-700">
              Activate
            </button>
          )}
          <button
            type="button"
            onClick={() => setShowEdit(true)}
            className="rounded border px-3 py-1"
          >
            Edit details
          </button>
          <button
            type="button"
            onClick={() => setShowChangePlan(true)}
            className="rounded border px-3 py-1"
          >
            Change plan
          </button>
          <button
            type="button"
            onClick={() => setShowBalance(true)}
            className="rounded bg-blue-600 px-3 py-1 text-white"
          >
            Adjust balance
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <Card title="Balance" value={`$${data.balance}`} sub="Prepaid" />
        <Card title="Plan" value={data.plan.name} sub={`${data.plan.plan_type} · $${data.plan.base_rate}/SMS`} />
        <Card
          title="Messages"
          value={String(data.messages_sent_month)}
          sub={`${data.messages_sent_today} today`}
        />
        <Card title="Phone" value={data.phone || '—'} />
        <Card title="Sender ID" value={data.sender_id || '—'} sub={`Timezone ${data.timezone}`} />
        <Card
          title="Webhook"
          value={data.webhook_url || '—'}
          sub={data.last_message_sent ? `Last msg ${formatDateTime(data.last_message_sent)}` : 'No messages yet'}
        />
      </div>

      <button
        type="button"
        onClick={() => navigate(0)}
        className="mt-4 text-xs text-slate-500 hover:underline"
      >
        Refresh
      </button>

      <BalanceAdjustModal
        open={showBalance}
        customer={data}
        onClose={() => setShowBalance(false)}
        onSaved={(updated) => setData(updated)}
      />
      <CustomerEditModal
        open={showEdit}
        customer={data}
        onClose={() => setShowEdit(false)}
        onSaved={(updated) => setData(updated)}
      />
      <ChangePlanModal
        open={showChangePlan}
        customer={data}
        onClose={() => setShowChangePlan(false)}
        onSaved={(updated) => setData(updated)}
      />
    </section>
  );
}

function Card({ title, value, sub }: { title: string; value: string; sub?: string }) {
  return (
    <div className="rounded-lg border bg-white p-4 shadow-sm">
      <div className="text-xs uppercase text-slate-500">{title}</div>
      <div className="mt-1 break-all text-base font-medium text-slate-900">{value}</div>
      {sub && <div className="mt-0.5 text-xs text-slate-500">{sub}</div>}
    </div>
  );
}
